/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { Button, Card, FieldGroup, Select, Table } from "core/components";
import formatter from "core/formatter";
import { CostItemFormData, CostLogFormData, CostLogItemFormData, CostLogStatus, TaskFormData } from "core/schema";
import CostLogItemsContext from "./CostLogItemsContext";
import CostLogContext from "./CostLogContext";

type CostLogItemsFormProps = {
  items: Array<CostLogItemFormData>;
  cost_items: Array<CostItemFormData>;
  task: TaskFormData;
  costLogStatus: CostLogStatus;
};

const SelectField = ({ index, cost_items, setSkipPageReset }) => {
  const { selectedItems, setSelectedItems } = React.useContext(CostLogItemsContext);
  const {
    control,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext();
  const fieldArrayName = "cost_log_items_attributes";
  const fieldItem = `${fieldArrayName}.${index}`;
  const costItem = watch(fieldItem);
  const [selectedCostItem, setSelectedCostItem] = React.useState<CostItemFormData>(cost_items.find((cost_item) => cost_item.id == costItem?.item_id));

  const addToSelectedItems = (costItem) => {
    if (selectedItems.find((i) => i?.item_id == costItem?.id)) {
      return;
    } else {
      setSelectedItems((value) => [...value, { item_id: costItem?.id }]);
    }
  };

  const removeFromSelectedItems = (costItem) => {
    if (selectedItems.find((selectedItem) => selectedItem?.item_id == costItem?.id)) {
      setSelectedItems(selectedItems.filter((selectedItem) => selectedItem?.item_id != costItem?.id) || []);
    }
  };

  React.useEffect(() => {
    if (selectedCostItem) {
      addToSelectedItems(selectedCostItem);
    }
  }, [selectedCostItem]);

  return (
    <Controller
      name={`${fieldArrayName}.${index}.item_id`}
      control={control}
      render={({ field }) => (
        <Select
          {...field}
          placeholder="Select Item"
          options={cost_items.filter(
            (cost_item) => !selectedItems.find((selected) => selected?.item_id == cost_item?.id && field?.value != cost_item?.id) || null
          )}
          getOptionLabel={(option) => `${option.code} - ${option.name}`}
          getOptionValue={(option) => option.id}
          isOptionDisabled={(option) => !option.active}
          onChange={(option, actionMeta) => {
            field.onChange(option?.id);

            if (selectedCostItem) {
              removeFromSelectedItems(selectedCostItem);
            }

            setSelectedCostItem(option);

            if (actionMeta.action == "select-option") {
              setValue(`${fieldItem}.unit_measure_id`, option?.unit_measure_id);
              setValue(`${fieldItem}.unit_measure_code`, option?.unit_measure_code);
              setValue(`${fieldItem}.budgeted_quantity`, 0);
              setValue(`${fieldItem}.budgeted_unit_rate`, option?.unit_rate);
              addToSelectedItems(option);
            } else {
              setValue(`${fieldItem}.unit_measure_id`, null);
              setValue(`${fieldItem}.unit_measure_code`, null);
              setValue(`${fieldItem}.budgeted_quantity`, null);
              setValue(`${fieldItem}.budgeted_unit_rate`, null);
              removeFromSelectedItems(option || actionMeta?.removedValues[0]);
            }
          }}
          ref={field.ref}
          onBlur={() => setSkipPageReset(true)}
          value={cost_items.find((cost_item) => cost_item.id == field.value) || null}
          errorMessage={errors[fieldArrayName]?.[index]?.item_id?.message}
        />
      )}
    />
  );
};

const NumberField = ({ field, setSkipPageReset, errorMessage }) => {
  return (
    <IMaskInput
      mask={Number}
      padFractionalZeros={true}
      thousandsSeparator=","
      radix="."
      unmask={true}
      onAccept={(value) => {
        field.onChange(value);
      }}
      value={field.value?.toString()}
      inputRef={field.ref}
      onBlur={() => setSkipPageReset(true)}
      className="field"
      placeholder="0.00"
      signed
      max={999999999}
      style={errorMessage ? { borderColor: "red" } : {}}
    />
  );
};

const CostLogItemsForm = ({ items, cost_items, task, costLogStatus }: CostLogItemsFormProps): JSX.Element => {
  const { setItemsForDestruction } = React.useContext(CostLogContext);
  const {
    control,
    register,
    resetField,
    getValues,
    formState: { errors }
  } = useFormContext<CostLogFormData>();
  const fieldArrayName = "cost_log_items_attributes";
  const { fields, append, remove } = useFieldArray<CostLogItemFormData>({
    control,
    name: fieldArrayName,
    keyName: "key"
  });
  const [selectedItems, setSelectedItems] = React.useState<Array<CostLogItemFormData>>(items);
  const [skipPageReset, setSkipPageReset] = React.useState(false);
  const isChangeOrderForm = location.href.includes("new_change_order");

  const canAppendItem = () => {
    if (isChangeOrderForm || items?.[0]?.change_order_task_item_id || (items?.[0]?.id && costLogStatus != CostLogStatus.Draft)) return false;
    return fields.length < cost_items.length;
  };

  const removeFromSelectedItems = (index: number) => {
    if (selectedItems.find((_selectedItem, i) => i == index)) {
      setSelectedItems(selectedItems.filter((_selectedItem, i) => i != index) || []);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: (_props) => {
          return <></>;
        }
      },
      {
        Header: "Task",
        accessor: "task",
        Cell: (props) => {
          return (
            <>
              <div className="text-bold text-truncate" title={props.value.code} data-bs-toggle="tooltip">
                {props.value.code}
              </div>
              <p className="text-truncate" title={props.value.name} data-bs-toggle="tooltip">
                {props.value.name}
              </p>
            </>
          );
        }
      },
      {
        Header: "Item",
        accessor: "item",
        style: {
          width: "20%"
        },
        Cell: (props) => {
          const id = props.row.values.id;
          const project_task_item_id = props.row.original.project_task_item_id;
          const change_order_task_item_id = props.row.original.change_order_task_item_id;
          const index = props.row.index;
          const item = props.value;
          let remarks;

          if (id && costLogStatus != CostLogStatus.Draft) {
            remarks = (
              <>
                {props.row.original.remarks != "" && (
                  <>
                    <p className="text-center text-bold mt-2">Remarks</p>
                    <p className="text-wrap text-break">
                      {props.row.original.remarks}
                    </p>
                  </>
                )}
              </>
            );
          } else {
            remarks = (
              <FieldGroup>
                <input {...register(`${fieldArrayName}.${index}.remarks`)} className="field" placeholder="Remarks" />
              </FieldGroup>
            );
          }

          if (id || project_task_item_id || change_order_task_item_id) {
            return (
              <>
                <div className="text-bold text-truncate" title={item.code} data-bs-toggle="tooltip">
                  {item.code}
                </div>
                <p className="text-truncate" title={item.name} data-bs-toggle="tooltip">
                  {item.name}
                </p>
                {remarks}
              </>
            );
          } else {
            return (
              <>
                <SelectField index={index} cost_items={cost_items} setSkipPageReset={setSkipPageReset} />
                {remarks}
              </>
            );
          }
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure_code",
        Cell: (props) => {
          const index = props.row.index;
          return <p className="text-center">{props.value || getValues(`${fieldArrayName}.${index}.unit_measure_code`)}</p>;
        }
      },
      {
        Header: "Budgeted Quantity",
        accessor: "budgeted_quantity",
        Cell: (props) => {
          const index = props.row.index;
          return <p className="text-center">{props.value || getValues(`${fieldArrayName}.${index}.budgeted_quantity`)}</p>;
        }
      },
      {
        Header: "Budgeted Unit Rate",
        accessor: "budgeted_unit_rate",
        Cell: (props) => {
          const index = props.row.index;
          const budgetedUnitRate = formatter.format(props.value || getValues(`${fieldArrayName}.${index}.budgeted_unit_rate`) || 0);
          return <p className="text-right">{budgetedUnitRate}</p>;
        }
      },
      {
        Header: "Current Quantity",
        accessor: "actual_quantity",
        Cell: (props) => {
          const actualQuantity = props.value || "0.0";
          return <p className="text-right">{actualQuantity}</p>;
        }
      },
      {
        Header: "Current Amount",
        accessor: "actual_cost",
        Cell: (props) => {
          const actualCost = formatter.format(props.value || 0);
          return <p className="text-right">{actualCost}</p>;
        }
      },
      {
        Header: "New Used Quantity",
        accessor: "used_quantity",
        Cell: (props) => {
          const id = props.row.values.id;
          const index = props.row.index;
          const usedQuantity = props.value;
          if (id && costLogStatus != CostLogStatus.Draft) {
            return <p className="text-right">{usedQuantity}</p>;
          } else {
            return (
              <Controller
                name={`${fieldArrayName}.${index}.used_quantity`}
                control={control}
                render={({ field }) => (
                  <NumberField
                    field={field}
                    setSkipPageReset={setSkipPageReset}
                    errorMessage={props.errors?.[fieldArrayName]?.[index]?.used_quantity?.message}
                  />
                )}
              />
            );
          }
        }
      },
      {
        Header: "New Used Amount",
        accessor: "used_amount",
        Cell: (props) => {
          const id = props.row.values.id;
          const index = props.row.index;
          if (id && costLogStatus != CostLogStatus.Draft) {
            const usedAmount = formatter.format(props.value);
            return <p className="text-right">{usedAmount}</p>;
          } else {
            return (
              <Controller
                name={`${fieldArrayName}.${index}.used_amount`}
                control={control}
                render={({ field }) => (
                  <NumberField
                    field={field}
                    setSkipPageReset={setSkipPageReset}
                    errorMessage={props.errors?.[fieldArrayName]?.[index]?.used_amount?.message}
                  />
                )}
              />
            );
          }
        }
      },
      {
        accessor: "actions",
        style: {
          width: "5%"
        },
        Cell: (props) => {
          const index = props.row.index;
          const id = props.row.values.id;
          if (costLogStatus == CostLogStatus.Draft) {
            return (
              <Button
                className="button--danger"
                icon="minus"
                iconProps={{ className: "svg-icon h5" }}
                onClick={() => {
                  if (id) {
                    const costLogItem = selectedItems.find((selectedItem) => selectedItem?.id == id);
                    if (costLogItem) setItemsForDestruction((value) => [...value, { ...costLogItem, _destroy: "1" }]);
                  }
                  resetField(`${fieldArrayName}.${index}`, { defaultValue: {} });
                  removeFromSelectedItems(index);
                  remove(index);
                }}
              />
            );
          } else {
            return <></>;
          }
        }
      }
    ],
    []
  );

  return (
    <React.StrictMode>
      <CostLogItemsContext.Provider value={{ selectedItems, setSelectedItems }}>
        <Card>
          <Card.Header title="Items">
            <h4 className="h mr-3">Total Items: {fields.length}</h4>
          </Card.Header>
          <Card.Body>
            <Table columns={columns} data={fields} skipPageReset={skipPageReset} errors={errors} />
            {canAppendItem() && (
              <Button
                className="button--brand mt-4"
                title="Add Item"
                icon="plus"
                type="button"
                onClick={() => {
                  append({ task, task_id: task.id });
                }}
              />
            )}
          </Card.Body>
        </Card>
      </CostLogItemsContext.Provider>
    </React.StrictMode>
  );
};

export default CostLogItemsForm;
